import { useState, useEffect, useCallback } from 'react';
import { DataTable, Column } from '../components/DataTable';
import { UserForm } from '../components/UserForm';
import { userService, User, UserCreatePayload, UserUpdatePayload } from '../services/users';
import { companyService, Company } from '../services/companies';
import { ApiError } from '../services/api';

type ViewMode = 'list' | 'create' | 'edit';

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [filterCompanyId, setFilterCompanyId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, companiesData] = await Promise.all([
        userService.list(filterCompanyId || undefined),
        companyService.list(),
      ]);
      setUsers(usersData);
      setCompanies(companiesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filterCompanyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function showSuccess(message: string) {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  function getErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 409) {
        const details = err.details as Record<string, string> | undefined;
        if (details?.field === 'email') {
          return 'This email address is already registered. Each user must have a unique email.';
        }
        return err.message;
      }
      if (err.status === 422) {
        const details = err.details as { fields?: Record<string, string> } | undefined;
        if (details?.fields) {
          return Object.values(details.fields).join('. ');
        }
        return err.message;
      }
      return err.message;
    }
    return err instanceof Error ? err.message : 'An unexpected error occurred';
  }

  async function handleCreate(data: UserCreatePayload | UserUpdatePayload) {
    setError(null);
    setIsSubmitting(true);
    try {
      await userService.create(data as UserCreatePayload);
      setViewMode('list');
      showSuccess('User created successfully');
      await fetchData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleUpdate(data: UserCreatePayload | UserUpdatePayload) {
    if (!selectedUser) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await userService.update(selectedUser.id, data as UserUpdatePayload);
      setViewMode('list');
      setSelectedUser(null);
      showSuccess('User updated successfully');
      await fetchData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(user: User) {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${user.name} ${user.last_name}" (${user.email})? This action cannot be undone.`,
    );
    if (!confirmed) return;

    setError(null);
    try {
      await userService.delete(user.id);
      showSuccess('User deleted successfully');
      await fetchData();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }

  function handleEdit(user: User) {
    setSelectedUser(user);
    setViewMode('edit');
    setError(null);
  }

  function handleCancel() {
    setViewMode('list');
    setSelectedUser(null);
    setError(null);
  }

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (u) => `${u.name} ${u.last_name}`,
    },
    { key: 'email', header: 'Email' },
    {
      key: 'user_type',
      header: 'Type',
      render: (u) => <span className={`badge badge-${u.user_type}`}>{u.user_type}</span>,
      width: '100px',
    },
    {
      key: 'company_name',
      header: 'Company',
      render: (u) => u.company_name || '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <span className={`badge badge-${u.status}`}>
          {u.status}
        </span>
      ),
      width: '90px',
    },
  ];

  if (viewMode === 'create') {
    return (
      <div>
        <div className="page-header">
          <h1>Users</h1>
        </div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <UserForm
          companies={companies}
          onSubmit={handleCreate}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </div>
    );
  }

  if (viewMode === 'edit' && selectedUser) {
    return (
      <div>
        <div className="page-header">
          <h1>Users</h1>
        </div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <UserForm
          user={selectedUser}
          companies={companies}
          onSubmit={handleUpdate}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header__row">
          <div>
            <h1>Users</h1>
            <p>Manage portal users and access control.</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => { setViewMode('create'); setError(null); }}
          >
            Add User
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {successMessage && <div className="alert alert-success" role="status">{successMessage}</div>}

      <div className="filter-bar">
        <label htmlFor="company-filter">Filter by company:</label>
        <select
          id="company-filter"
          value={filterCompanyId}
          onChange={(e) => setFilterCompanyId(e.target.value)}
        >
          <option value="">All companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.usual_name}
            </option>
          ))}
        </select>
      </div>

      <DataTable<User>
        columns={columns}
        data={users}
        keyField="id"
        loading={loading}
        emptyMessage="No users found."
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  );
}
